import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { gcLineItemKeys } from "@/lib/query";

export type GcLineItemType =
  | "gc_fee"
  | "general_conditions"
  | "liability_insurance"
  | "general_requirements"
  | "owner_contingency"
  | "gc_contingency"
  | "custom";

export type GcLineItemBillingMethod =
  | "fixed_lump_sum"
  | "percent_of_hard_costs"
  | "manual_per_period";

export interface GcLineItem {
  id: string;
  orgId: string;
  projectId: string;
  lineType: GcLineItemType;
  label: string;
  budgetAmount: string;
  billingMethod: GcLineItemBillingMethod;
  percentOfHardCosts: string | null;
  ownerFacing: boolean;
  retainageApplies: boolean;
  retentionPercentOverride: string | null;
  costCodeId: string | null;
  displayOrder: number;
  requiresReasonPerDraw: boolean;
  notes: string | null;
  budgetLockedAt: string | null;
  budgetLockedByUserId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GcLineItemInput {
  lineType: GcLineItemType;
  label: string;
  budgetAmount: string;
  billingMethod: GcLineItemBillingMethod;
  percentOfHardCosts?: string | null;
  ownerFacing?: boolean;
  retainageApplies?: boolean;
  retentionPercentOverride?: string | null;
  costCodeId?: string | null;
  displayOrder?: number;
  requiresReasonPerDraw?: boolean;
  notes?: string | null;
}

export type GcLineItemUpdate = Partial<GcLineItemInput>;

export interface GcCostTrackerRow {
  baseline: GcLineItem;
  periods: Array<{
    payAppId: string;
    applicationNo: number | null;
    periodEnd: string | null;
    status: string;
    lineItemId: string;
    thisPeriod: number;
    storedMaterials: number;
    cumulative: number;
    drawReason: string | null;
    feeOverrideReason: string | null;
  }>;
  totals: {
    budget: number;
    billedToDate: number;
    balance: number;
    pctComplete: number;
  };
}

export function useProjectGcLineItems(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? gcLineItemKeys.project(projectId) : gcLineItemKeys.all,
    queryFn: async () => {
      const res = await apiFetch<{ data: GcLineItem[] }>(`/projects/${projectId}/gc-line-items`);
      return res.data;
    },
    enabled: !!projectId,
  });
}

export function useCreateGcLineItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: GcLineItemInput) => {
      const res = await apiPost<{ data: GcLineItem }>(
        `/projects/${projectId}/gc-line-items`,
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gcLineItemKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: gcLineItemKeys.tracker(projectId) });
    },
  });
}

export function useUpdateGcLineItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: GcLineItemUpdate }) => {
      const res = await apiPatch<{ data: GcLineItem }>(`/gc-line-items/${id}`, updates);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gcLineItemKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: gcLineItemKeys.tracker(projectId) });
    },
  });
}

export function useDeleteGcLineItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/gc-line-items/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gcLineItemKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: gcLineItemKeys.tracker(projectId) });
    },
  });
}

export function useUnlockGcLineItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await apiPost<{ data: GcLineItem }>(`/gc-line-items/${id}/unlock`, { reason });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gcLineItemKeys.project(projectId) });
      qc.invalidateQueries({ queryKey: gcLineItemKeys.tracker(projectId) });
    },
  });
}

export function useReorderGcLineItems(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const res = await apiPost<{ data: GcLineItem[] }>(
        `/projects/${projectId}/gc-line-items/reorder`,
        { orderedIds },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gcLineItemKeys.project(projectId) });
    },
  });
}

export function useGcCostTracker(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? gcLineItemKeys.tracker(projectId) : gcLineItemKeys.all,
    queryFn: () => apiFetch<{ rows: GcCostTrackerRow[] }>(`/projects/${projectId}/gc-cost-tracker`),
    enabled: !!projectId,
  });
}
